// src/components/tiptap/extensions/WhatDoYouHearNode.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer, NodeViewWrapper } from "@tiptap/react";
import * as FiIcons from "react-icons/fi";
import { supabase } from "../../../config/supabase";
import CelebrationOverlay from "../../common/CelebrationOverlay.jsx";
import SafeIcon from "../../../common/SafeIcon.jsx";
import { emitToolCompleted } from "../../../lib/mmsToolEvents";
import StickerBadgeBar from "../../common/stickers/StickerBadgeBar.jsx";

const {
  FiHeadphones,
  FiSave,
  FiTrash2,
  FiSettings,
  FiRepeat,
  FiVolume2,
  FiVolumeX,
  FiPlay,
  FiCheckSquare,
  FiSquare,
} = FiIcons;

const cls = (...xs) => xs.filter(Boolean).join(" ");
const uniq = (arr) => Array.from(new Set((arr || []).filter(Boolean)));
const shuffle = (a) => {
  const arr = a.slice();
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

// Build answer options based on selectedNames:
// - If >=4 selected: 1 correct (row.name) + 3 incorrect sampled from selectedNames (excluding correct), then shuffle.
// - If <4 selected: show all selectedNames (ensure correct is present), then shuffle.
function buildNameChoices(correctName, selectedNames) {
  const pool = Array.from(new Set((selectedNames || []).filter(Boolean)));
  const withCorrect = pool.includes(correctName)
    ? pool.slice()
    : [correctName, ...pool];

  if (withCorrect.length < 4) return shuffle(withCorrect);

  const incorrects = withCorrect.filter((n) => n !== correctName);
  const picks = [];
  const temp = incorrects.slice();
  for (let i = 0; i < 3 && temp.length > 0; i++) {
    const j = Math.floor(Math.random() * temp.length);
    picks.push(temp.splice(j, 1)[0]);
  }
  return shuffle([correctName, ...picks]);
}

const SFX = {
  correct:
    "https://vdjxrusqywiraonfwujq.supabase.co/storage/v1/object/public/mmsmedia/audio/game-effects/correct.mp3",
  incorrect:
    "https://vdjxrusqywiraonfwujq.supabase.co/storage/v1/object/public/mmsmedia/audio/game-effects/incorrect.mp3",
  celebration:
    "https://vdjxrusqywiraonfwujq.supabase.co/storage/v1/object/public/mmsmedia/audio/game-effects/celebration.mp3",
};

const isFormControl = (el) =>
  el &&
  (el.tagName === "SELECT" ||
    el.tagName === "INPUT" ||
    el.tagName === "TEXTAREA" ||
    el.isContentEditable);

/* ─────────────────────────────────────────────────────────────
   PERF: lightweight per-page caches for dropdown data
   - facets: overall/category list
   - names: list_aural_names results per (overall, category)
   Keeps behavior identical; just avoids repeat calls across mounts/blocks.
   ──────────────────────────────────────────────────────────── */

const facetsCache = { data: null, fetchedAt: 0 };
let facetsInflight = null;

async function getFacetsOnce() {
  // 5 min TTL (safe; facets rarely change during a single session)
  if (facetsCache.data && Date.now() - facetsCache.fetchedAt < 5 * 60 * 1000) {
    return facetsCache.data;
  }
  if (!facetsInflight) {
    facetsInflight = (async () => {
      const { data, error } = await supabase
        .from("aural_library_facets")
        .select("overall_category, category");
      facetsInflight = null;
      if (error) throw error;
      facetsCache.data = data || [];
      facetsCache.fetchedAt = Date.now();
      return facetsCache.data;
    })();
  }
  return facetsInflight;
}

const namesCache = new Map(); // key -> Promise<string[]>
function namesKey(overallCategory, category) {
  return `${overallCategory ?? ""}\u0001${category ?? ""}`;
}
function getNamesOnce(overallCategory, category) {
  const key = namesKey(overallCategory, category);
  if (namesCache.has(key)) return namesCache.get(key);

  const p = (async () => {
    const { data, error } = await supabase.rpc("list_aural_names", {
      p_overall_category: overallCategory ?? null,
      p_category: category ?? null,
    });
    if (error) throw error;
    return uniq((data || []).map((r) => r?.name).filter(Boolean));
  })().catch(() => []);

  namesCache.set(key, p);
  return p;
}

function WhatDoYouHearBlock({ node, updateAttributes, editor, deleteNode }) {
  const attrs = node.attrs || {};
  const storage =
    editor?.storage?.mms ?? editor?.storage?.mmsContext ?? {};
  const { schoolId, userId, isGlobalMute } = storage;

  const canDelete = !!userId && !!editor?.isEditable; // teachers only

  // ui state
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [starting, setStarting] = useState(false);
  const [showCategoryHint, setShowCategoryHint] = useState(false);

  // filters
  const [overallOptions, setOverallOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [nameOptions, setNameOptions] = useState([]);

  // game state
  const [deck, setDeck] = useState([]);
  const [qIndex, setQIndex] = useState(0);
  const [choices, setChoices] = useState([]);
  const [picked, setPicked] = useState(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [done, setDone] = useState(false);

  const audioRef = useRef(null);

  // SFX (reuse Audio elements; don’t recreate)
  const sfx = useRef({ correct: null, incorrect: null, celebration: null });
  const saved = !!attrs.recordId;

  // track image load/error to avoid broken icon flash
  const [imageOk, setImageOk] = useState(true);
  function hasImageUrl(u) {
    if (!u) return false;
    const s = String(u).trim();
    if (s === "" || s.toLowerCase() === "null" || s.toLowerCase() === "undefined")
      return false;
    return true;
  }

  // bootstrap defaults into node attrs
  useEffect(() => {
    const patch = {};
    if (!attrs.noteBlockId) patch.noteBlockId = crypto.randomUUID();
    if (!attrs.mode) patch.mode = "practice";
    if (typeof attrs.numQuestions !== "number") patch.numQuestions = 5;
    if (typeof attrs.sfxMuted !== "boolean") patch.sfxMuted = false;
    if (!Array.isArray(attrs.selectedNames)) patch.selectedNames = [];
    if (Object.keys(patch).length) updateAttributes(patch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Preload SFX once
  useEffect(() => {
    if (!sfx.current.correct) sfx.current.correct = new Audio(SFX.correct);
    if (!sfx.current.incorrect) sfx.current.incorrect = new Audio(SFX.incorrect);
    if (!sfx.current.celebration) sfx.current.celebration = new Audio(SFX.celebration);
  }, []);

  // Cleanup audio on unmount (prevents leaking audio handles across node removals)
  useEffect(() => {
    return () => {
      const a = audioRef.current;
      if (a) {
        try {
          a.pause();
          a.currentTime = 0;
        } catch {
          /* ignore */
        }
        audioRef.current = null;
      }
      // Leave sfx Audio elements cached (they’re tiny and reused elsewhere in the session)
    };
  }, []);

  function playSfx(kind) {
    if (attrs.sfxMuted) return;
    const a = sfx.current[kind];
    if (!a) return;
    try {
      a.currentTime = 0;
      a.play();
    } catch {
      /* no-op */
    }
  }

  // Load dropdown/option data (cached) and keep node.selectedNames in sync
  useEffect(() => {
    let alive = true;

    const run = async () => {
      try {
        const facets = await getFacetsOnce();
        if (!alive) return;

        setOverallOptions(uniq(facets.map((r) => r.overall_category)));

        const categoryPool = facets.filter(
          (r) => !attrs.overallCategory || r.overall_category === attrs.overallCategory
        );
        setCategoryOptions(uniq(categoryPool.map((r) => r.category)));

        const names = await getNamesOnce(attrs.overallCategory ?? null, attrs.category ?? null);
        if (!alive) return;

        setNameOptions(names);

        // Sync selectedNames:
        // - If empty: default to "all"
        // - If existing: drop names that no longer exist in scope
        const current = Array.isArray(attrs.selectedNames) ? attrs.selectedNames : [];
        let nextSelected =
          current.length === 0 ? names.slice() : current.filter((n) => names.includes(n));

        if (names.length > 0 && nextSelected.length === 0) nextSelected = names.slice();

        const a = (current || []).join("\u0001");
        const b = (nextSelected || []).join("\u0001");
        if (a !== b) updateAttributes({ selectedNames: nextSelected });
      } catch {
        // ignore
      }
    };

    // Defer to idle if possible (snappier editor open)
    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      const id = window.requestIdleCallback(run, { timeout: 1500 });
      return () => {
        alive = false;
        try {
          window.cancelIdleCallback(id);
        } catch {
          /* ignore */
        }
      };
    }

    const t = setTimeout(run, 0);
    return () => {
      alive = false;
      clearTimeout(t);
    };
    // IMPORTANT: do NOT depend on attrs.selectedNames here (prevents noisy refetch loops)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attrs.overallCategory, attrs.category, updateAttributes]);

  async function handleSave() {
    const effectiveLessonNoteId = attrs.lessonNoteId ?? storage.lessonNoteId ?? null;
    const effectiveSchoolId = schoolId ?? storage.schoolId ?? null;
    const effectiveUserId = userId ?? storage.userId ?? null;
    const effectiveNoteBlockId =
      attrs.noteBlockId ?? (crypto?.randomUUID?.() || String(Date.now()));

    const patch = {};
    if (!attrs.lessonNoteId && effectiveLessonNoteId) patch.lessonNoteId = effectiveLessonNoteId;
    if (!attrs.noteBlockId && effectiveNoteBlockId) patch.noteBlockId = effectiveNoteBlockId;
    if (Object.keys(patch).length) updateAttributes(patch);

    if (!effectiveLessonNoteId) {
      alert("Missing context: need lessonNoteId to save this tool.");
      return;
    }

    if (!attrs.overallCategory) {
      setShowCategoryHint(true);
      alert("Please choose an Overall Category before saving.");
      return;
    }

    setSaving(true);
    const { data, error } = await supabase.rpc("get_or_create_aural_game_instance", {
      p_note_block_id: effectiveNoteBlockId,
      p_school_id: effectiveSchoolId,
      p_lesson_note_id: effectiveLessonNoteId,
      p_created_by: effectiveUserId,
      p_overall_category: attrs.overallCategory ?? null,
      p_category: attrs.category ?? null,
      p_level: attrs.level ?? null, // backward compat
      p_mode: attrs.mode ?? "practice",
      p_question_count: attrs.numQuestions === 0 ? 0 : attrs.numQuestions ?? 5,
    });
    setSaving(false);

    if (error) {
      if (error.code === "23502" && /overall_category/i.test(error.message || "")) {
        setShowCategoryHint(true);
        alert("Please choose an Overall Category before saving.");
        return;
      }
      alert(error.message);
      return;
    }

    updateAttributes({ recordId: data.id });
  }

  async function handleDelete() {
    if (!attrs.recordId) {
      deleteNode();
      return;
    }
    if (!confirm("Delete this tool from the note?")) return;
    setDeleting(true);
    const { error } = await supabase.rpc("delete_aural_game_instance", { p_id: attrs.recordId });
    setDeleting(false);
    if (error) return alert(error.message);
    deleteNode();
  }

  function nudgeMute() {
    if (isGlobalMute) alert("Global mute is ON. Toggle it to hear audio.");
  }

  async function startGame() {
    setStarting(true);
    setDone(false);
    setCorrectCount(0);
    setPicked(null);
    setQIndex(0);

    // Fetch only the columns we actually use (less payload)
    let q = supabase
      .from("aural_files_library")
      .select("id,name,audio_url,image_url,overall_category,category")
      .eq("is_active", true);

    if (attrs.overallCategory) q = q.eq("overall_category", attrs.overallCategory);
    if (attrs.category) q = q.eq("category", attrs.category);

    // Only filter by name if user has narrowed selection (vs. "all names" for current scope)
    const selected = Array.isArray(attrs.selectedNames)
      ? attrs.selectedNames.filter(Boolean)
      : [];
    const allNames = nameOptions; // already scoped to overall+category
    const isAllSelected =
      selected.length > 0 &&
      selected.length === allNames.length &&
      selected.every((n) => allNames.includes(n));

    if (selected.length > 0 && !isAllSelected) q = q.in("name", selected);

    const { data, error } = await q.range(0, 9999);
    setStarting(false);
    if (error) return alert(error.message);

    const items = shuffle(data || []);
    const count =
      (attrs.numQuestions ?? 5) === 0
        ? items.length
        : Math.max(1, attrs.numQuestions ?? 5);

    const chosen = items.slice(0, count);
    setDeck(chosen);

    if (chosen[0]) {
      setChoices(buildNameChoices(chosen[0].name, attrs.selectedNames || []));
    }
  }

  function playAudio(opts = {}) {
    const { nudge = true } = opts;
    const row = deck[qIndex];
    if (!row?.audio_url) return;

    if (isGlobalMute) {
      if (nudge) nudgeMute();
      return;
    }

    // Reuse a single Audio element (fast, avoids churn)
    if (!audioRef.current) {
      const a = new Audio(row.audio_url);
      a.preload = "auto";
      audioRef.current = a;
    } else {
      const a = audioRef.current;
      if (a.src !== row.audio_url) {
        a.pause();
        a.src = row.audio_url;
        // no need to call load() explicitly; setting src is enough in modern browsers
      } else {
        // same file replay
        try {
          a.pause();
          a.currentTime = 0;
        } catch {
          /* ignore */
        }
      }
    }

    audioRef.current.play().catch(() => {});
  }

  function stopQuestionAudio() {
    const a = audioRef.current;
    if (!a) return;
    try {
      a.pause();
    } catch {}
    try {
      a.currentTime = 0;
    } catch {}
  }

  useEffect(() => {
    if (deck.length > 0 && !done) {
      setImageOk(true);
      playAudio({ nudge: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qIndex, deck, done]);

  function pick(ans) {
    const row = deck[qIndex];
    if (!row) return;
    setPicked(ans);

    const correct = ans === row.name;
    if (correct) setCorrectCount((c) => c + 1);

    if (qIndex === deck.length - 1) stopQuestionAudio();

    if ((attrs.mode ?? "practice") === "practice") {
      playSfx(correct ? "correct" : "incorrect");
    }
    if ((attrs.mode ?? "practice") === "quiz") {
      next(correct);
    }
  }

  function next(lastWasCorrect = null) {
    const n = qIndex + 1;
    if (n >= deck.length) {
      stopQuestionAudio();

      const finalCorrect =
        (attrs.mode ?? "practice") === "quiz" && lastWasCorrect === true
          ? correctCount + 1
          : correctCount;

      setDone(true);

      if ((attrs.mode ?? "practice") === "quiz" && finalCorrect === deck.length) {
        playSfx("celebration");
        const detail = {
          noteBlockId: attrs?.noteBlockId,
          earnedFor: "Completed listening quiz",
          payload: { score: finalCorrect, total: deck.length },
        };
        try {
          emitToolCompleted(detail);
          window.dispatchEvent(new CustomEvent("mms:toolCompleted", { detail }));
        } catch {
          /* ignore */
        }
      }
      return;
    }

    setQIndex(n);
    setPicked(null);

    const row = deck[n];
    setChoices(buildNameChoices(row.name, attrs.selectedNames || []));

    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      } catch {
        /* ignore */
      }
    }
  }

  function resetSameSettings() {
    startGame();
  }

  function backToSettings() {
    stopQuestionAudio();
    setDeck([]);
    setQIndex(0);
    setPicked(null);
    setDone(false);
  }

  const perfectQuiz =
    done &&
    (attrs.mode ?? "practice") === "quiz" &&
    deck.length > 0 &&
    correctCount === deck.length;

  const allSelected =
    nameOptions.length > 0 &&
    Array.isArray(attrs.selectedNames) &&
    attrs.selectedNames.length === nameOptions.length;

  const noneSelected =
    !Array.isArray(attrs.selectedNames) || attrs.selectedNames.length === 0;

  function toggleName(n) {
    const curr = new Set(attrs.selectedNames || []);
    if (curr.has(n)) curr.delete(n);
    else curr.add(n);

    // keep order consistent
    const next = nameOptions.filter((x) => curr.has(x));
    updateAttributes({ selectedNames: next });
  }

  function selectAll() {
    updateAttributes({ selectedNames: nameOptions.slice() });
  }

  function clearAll() {
    updateAttributes({ selectedNames: [] });
  }

  return (
    <NodeViewWrapper
      as="div"
      contentEditable={false}
      role="group"
      tabIndex={-1}
      className="relative group w-full rounded-2xl bg-slate-50 shadow-sm p-4 border border-slate-200"
      data-note-block-id={attrs.noteBlockId}
      style={{ position: "relative" }}
      onMouseDownCapture={(e) => {
        if (!isFormControl(e.target)) e.preventDefault();
        e.stopPropagation();
      }}
      onTouchStartCapture={(e) => {
        if (!isFormControl(e.target)) e.preventDefault();
        e.stopPropagation();
      }}
    >
      {/* Sticker badges (top border/right, half outside) */}
      <div className="absolute right-3 top-0 translate-y-[-50%] z-10">
        <StickerBadgeBar noteBlockId={attrs.noteBlockId} max={10} />
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-orange-100 text-orange-600 rounded-xl flex items-center justify-center">
            <SafeIcon icon={FiHeadphones} className="w-6 h-6" />
          </div>
          <div>
            <div className="text-lg font-bold text-slate-800">What Do You Hear?</div>
            <div className="text-sm text-slate-500">
              Listen and choose the correct answer.
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-start sm:justify-end gap-2 sm:flex-shrink-0">
          {/* Debug toggle */}
          <button
            className="px-2 py-1 rounded-lg text-[11px] font-medium border bg-white/80 border-slate-300 hover:bg-slate-100 transition-colors flex items-center gap-1"
            onClick={() => updateAttributes({ showDebug: !attrs.showDebug })}
            title={attrs.showDebug ? "Hide debug info" : "Show debug info"}
          >
            <SafeIcon
              icon={attrs.showDebug ? FiCheckSquare : FiSquare}
              className="w-4 h-4"
            />
            <span className="text-slate-600">Debug</span>
          </button>

          <button
            className="p-2 rounded-full text-slate-500 hover:bg-slate-200 hover:text-slate-700 transition-colors"
            onClick={() => updateAttributes({ sfxMuted: !attrs.sfxMuted })}
            title={attrs.sfxMuted ? "Unmute SFX" : "Mute SFX"}
          >
            <SafeIcon icon={attrs.sfxMuted ? FiVolumeX : FiVolume2} className="w-5 h-5" />
          </button>

          <button
            className={cls(
              "px-3 py-1.5 rounded-lg text-sm font-semibold border transition-all flex items-center gap-2",
              saved
                ? "bg-slate-200 text-slate-500 border-slate-200 cursor-default"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100 hover:border-slate-400"
            )}
            disabled={saving || saved}
            onClick={handleSave}
          >
            <SafeIcon icon={FiSave} />
            {saved ? "Saved" : saving ? "Saving..." : "Save"}
          </button>

          {canDelete && (
            <button
                className="p-2 rounded-full text-slate-500 hover:bg-red-100 hover:text-red-600 transition-colors opacity-50 group-hover:opacity-100"
                disabled={deleting}
                onClick={handleDelete}
                title="Delete Tool"
            >
                <SafeIcon icon={FiTrash2} className="w-5 h-5" />
            </button>
            )}
        </div>
      </div>

      {/* Config */}
      {deck.length === 0 ? (
        <div className="bg-white/80 rounded-xl p-4 border border-dashed">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">
                Mode
              </label>
              <select
                className="w-full border-slate-300 rounded-lg shadow-sm focus:ring-orange-500 focus:border-orange-500 transition"
                value={attrs.mode ?? "practice"}
                onChange={(e) => updateAttributes({ mode: e.target.value })}
              >
                <option value="practice">Practice</option>
                <option value="quiz">Quiz</option>
              </select>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">
                Overall Category
              </label>
              <select
                className="w-full border-slate-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 transition"
                value={attrs.overallCategory ?? ""}
                onChange={(e) => {
                  updateAttributes({
                    overallCategory: e.target.value || null,
                    category: null,
                    level: null,
                    selectedNames: [], // will repopulate to ALL in the effect
                  });
                }}
              >
                <option value="">All</option>
                {overallOptions.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
              {!attrs.overallCategory && showCategoryHint && (
                <div className="mt-1 text-xs text-amber-700">
                  Please choose an Overall Category to save. (The “All” option isn’t
                  allowed by your school’s current settings.)
                </div>
              )}
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">
                Category
              </label>
              <select
                className="w-full border-slate-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 transition"
                value={attrs.category ?? ""}
                onChange={(e) =>
                  updateAttributes({
                    category: e.target.value || null,
                    level: null,
                    selectedNames: [],
                  })
                }
              >
                <option value="">All</option>
                {categoryOptions.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </div>

            {/* Names multi-select (replaces Level) */}
            <div className="md:col-span-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-slate-600 mb-1 block">
                  Names
                </label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="text-[11px] px-2 py-1 rounded border border-slate-300 hover:bg-slate-100"
                    onClick={selectAll}
                    disabled={nameOptions.length === 0 || allSelected}
                    title="Select all names"
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    className="text-[11px] px-2 py-1 rounded border border-slate-300 hover:bg-slate-100"
                    onClick={clearAll}
                    disabled={nameOptions.length === 0 || noneSelected}
                    title="Clear selection"
                  >
                    Clear
                  </button>
                </div>
              </div>

              {nameOptions.length === 0 ? (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                  No names for this selection. Try a different Overall Category or
                  Category.
                </div>
              ) : (
                <div className="max-h-36 overflow-auto rounded-lg border border-slate-200 bg-white/80 p-2 grid grid-cols-1 sm:grid-cols-2 gap-1">
                  {nameOptions.map((n) => {
                    const checked = (attrs.selectedNames || []).includes(n);
                    return (
                      <label
                        key={n}
                        className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none"
                      >
                        <input
                          type="checkbox"
                          className="rounded border-slate-300"
                          checked={checked}
                          onChange={() => toggleName(n)}
                        />
                        <span className="truncate">{n}</span>
                      </label>
                    );
                  })}
                </div>
              )}

              <div className="text-[11px] text-slate-500 mt-1">
                {Array.isArray(attrs.selectedNames)
                  ? `${attrs.selectedNames.length} selected`
                  : "0 selected"}
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">
                # Questions
              </label>
              <select
                className="w-full border-slate-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 transition"
                value={String(attrs.numQuestions ?? 5)}
                onChange={(e) => updateAttributes({ numQuestions: Number(e.target.value) })}
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="0">All</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              className={cls(
                "w-full md:w-auto px-6 py-2.5 rounded-xl text-base font-semibold text-white shadow-lg transition-all transform",
                saved
                  ? "bg-orange-500 hover:bg-orange-600 active:scale-95"
                  : "bg-slate-400 cursor-not-allowed"
              )}
              onClick={startGame}
              disabled={!saved || starting}
              title={saved ? "Start!" : "Save first to enable Start"}
            >
              <span className="inline-flex items-center gap-2">
                <SafeIcon icon={FiPlay} />
                {starting ? "Loading…" : "Start"}
              </span>
            </button>
          </div>
        </div>
      ) : null}

      {/* Game */}
      {deck.length > 0 && !done && (
        <div className="space-y-3">
          <div className="flex items-center justify-between text-xs text-slate-500">
            <div>
              Question {qIndex + 1} / {deck.length}
            </div>
            {isGlobalMute && <div className="text-amber-600">Global mute is ON</div>}
          </div>

          <div className="flex items-center gap-2">
            <button
              className="px-3 py-1.5 rounded-lg text-sm font-semibold border bg-white/80 border-slate-300 hover:bg-slate-100 transition-colors flex items-center gap-2"
              onClick={playAudio}
            >
              <SafeIcon icon={FiPlay} /> Play
            </button>
            <button
              className="px-3 py-1.5 rounded-lg text-sm font-semibold border bg-white/80 border-slate-300 hover:bg-slate-100 transition-colors"
              onClick={nudgeMute}
            >
              Having trouble hearing?
            </button>
          </div>

          {/* Debug */}
          {attrs.showDebug && deck[qIndex] && (
            <div className="text-[11px] text-slate-500 mt-1">
              Debug · <span className="font-mono">{deck[qIndex].id}</span> —{" "}
              {deck[qIndex].name || "—"}
            </div>
          )}

          {choices.length < 2 && (
            <div className="text-xs text-amber-600">
              Not enough answer options—select more Names in the filter to get
              additional choices.
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {choices.map((ans) => {
              const isChoice = picked === ans;
              const isCorrect = (deck[qIndex]?.name ?? "") === ans;
              const showFeedback = (attrs.mode ?? "practice") === "practice" && picked;
              return (
                <button
                  key={ans}
                  className={cls(
                    "text-left rounded-xl border p-3 transition focus:outline-none focus:ring bg-white/80",
                    isChoice ? "border-indigo-500" : "border-slate-200",
                    showFeedback && isChoice && isCorrect && "bg-green-50",
                    showFeedback && isChoice && !isCorrect && "bg-red-50"
                  )}
                  onClick={() => pick(ans)}
                >
                  <div className="font-medium text-slate-800">{ans}</div>
                  {showFeedback && isChoice && (
                    <div className="text-xs mt-1 text-slate-600">
                      {isCorrect ? "✔ Correct" : "✖ Try the next one"}
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {(attrs.mode ?? "practice") === "practice" && picked && (
            <div className="flex items-center gap-2">
              <button
                className="px-3 py-1.5 rounded-lg text-sm font-semibold border bg-white/80 border-slate-300 hover:bg-slate-100 transition-colors"
                onClick={() => next(null)}
              >
                Next
              </button>
              {hasImageUrl(deck[qIndex]?.image_url) && imageOk && (
                <img
                  src={deck[qIndex].image_url}
                  alt="Explanation"
                  className="h-24 rounded-xl border"
                  onError={() => setImageOk(false)}
                />
              )}
            </div>
          )}
        </div>
      )}

      {/* Done */}
      {done && (
        <>
          <div className="bg-white/80 rounded-xl p-4 border">
            <div className="text-base font-semibold mb-1">All done!</div>
            <div className="text-sm mb-3">
              Score: {correctCount} / {deck.length} (
              {Math.round((correctCount / Math.max(1, deck.length)) * 100)}%)
            </div>
            <div className="flex items-center gap-2">
              <button
                className="px-3 py-1.5 rounded-lg text-sm font-semibold border bg-white/80 border-slate-300 hover:bg-slate-100 transition-colors flex items-center gap-2"
                onClick={resetSameSettings}
              >
                <SafeIcon icon={FiRepeat} /> Try Again
              </button>
              <button
                className="px-3 py-1.5 rounded-lg text-sm font-semibold border bg-white/80 border-slate-300 hover:bg-slate-100 transition-colors flex items-center gap-2"
                onClick={backToSettings}
              >
                <SafeIcon icon={FiSettings} /> New Settings
              </button>
            </div>
          </div>

          <CelebrationOverlay
            show={perfectQuiz}
            message="Perfect score! 🎉"
            duration={3200}
            fullScreen
            intensity="epic"
            onDone={backToSettings}
          />
        </>
      )}

      {/* Footer */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mt-3 text-[11px] text-slate-500 min-w-0">
        <div className="min-w-0 break-words">
        Saved: {saved ? "Yes" : "No"} • Note Block: {attrs.noteBlockId ?? "—"}
        </div>
        <div className="min-w-0 break-words">
        Lesson: {attrs.lessonNoteId ?? "—"}
        </div>
      </div>
    </NodeViewWrapper>
  );
}

// TipTap extension
const WhatDoYouHearNode = Node.create({
  name: "whatDoYouHear",
  group: "block",
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      noteBlockId: { default: null },
      lessonNoteId: { default: null },
      recordId: { default: null },
      mode: { default: "practice" },
      overallCategory: { default: null },
      category: { default: null },
      level: { default: null }, // kept for compatibility with existing records/RPC arg
      selectedNames: { default: [] },
      numQuestions: { default: 5 },
      sfxMuted: { default: false },
      showDebug: { default: false },
    };
  },

  parseHTML() {
    return [{ tag: "what-do-you-hear" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["what-do-you-hear", mergeAttributes(HTMLAttributes)];
  },
  addNodeView() {
    return ReactNodeViewRenderer(WhatDoYouHearBlock);
  },
});

export default WhatDoYouHearNode;